import { z } from "zod";

export enum LessonHost {
  GoogleMeets = "google_meets",
  Zoom = "zoom",
  OtherWay = "other_way",
  Ximly = "our_platform",
}

export enum PriceCurrency {
  Eur = "EUR",
  Gbp = "GBP",
  Pln = "PLN",
  Usd = "USD",
}

export function stringToCurrency(currency: string | undefined) {
  switch (currency) {
    case "EUR":
      return PriceCurrency.Eur;
    case "GBP":
      return PriceCurrency.Gbp;
    case "PLN":
      return PriceCurrency.Pln;
    case "USD":
      return PriceCurrency.Usd;
    default:
      return PriceCurrency.Usd;
  }
}

export const createSlotSchema = (t: any) =>
  z
    .object({
      start_time: z
        .string()
        .regex(/^\d{2}:\d{2}$/, t("forms:templates.validation.startTimeFormat")),
      end_time: z.string().regex(/^\d{2}:\d{2}$/, t("forms:templates.validation.endTimeFormat")),
      date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
      end_date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
      lesson_host: z.nativeEnum(LessonHost),
      communication_details: z
        .string()
        .max(255, t("forms:templates.validation.communicationDetailsMax", { length: 255 }))
        .optional(),
      overlap_gc: z.boolean().optional(),
    })
    .refine(
      (data) => {
        // Convert times to minutes for comparison
        const [startHour, startMinute] = data.start_time.split(":").map(Number);
        const [endHour, endMinute] = data.end_time.split(":").map(Number);

        if (endHour < startHour || (endHour === startHour && endMinute < startMinute)) {
          return true;
        }

        const startMinutes = startHour * 60 + startMinute;
        const endMinutes = endHour * 60 + endMinute;

        // Check if duration is at least 10 minutes
        return endMinutes - startMinutes >= 10;
      },
      {
        message: t("forms:templates.validation.durationMinimum"),
        path: ["end_time"],
      },
    )
    .refine(
      (data) => {
        return !(data.lesson_host === LessonHost.OtherWay && !data.communication_details);
      },
      {
        message: t("forms:templates.validation.communicationDetailsRequired"),
        path: ["communication_details"],
      },
    );

export type CreateSlotFormData = z.infer<ReturnType<typeof createSlotSchema>>;

export interface Req {
  end_time: string;
  lesson_host: LessonHost;
  communication_details?: string;
  overlap_gc?: boolean | number;
  start_time: string;
}

export interface SlotDetails {
  id: string;
  start_time: string;
  startTimeDateTimeString: string;
  endTimeDateTimeString: string;
  endTime: string;
  status: string;
  display_price: string;
  original_price: {
    amount: number;
    currency: string;
  };
  lessonType: string;
  lessonHost: string;
  communicationDetails?: string | undefined;
  lesson?: {
    id: string;
    workspace_id: string;
    subject: {
      id: string;
      name: string;
    };
    topic: string;
    participants: [
      {
        id: string;
        type: "student" | "tutor";
        name: string;
        avatar: string;
      },
    ];
  };
}
